import { Injectable } from '@angular/core';
import { Router } from "@angular/router";
import { AmplifyService } from "aws-amplify-angular";
import { AuthState } from "aws-amplify-angular/src/providers";
import { Observable, BehaviorSubject, Subject, ReplaySubject } from "rxjs";
import { take, map } from "rxjs/operators";

import { ConfigService } from "../services/config.service";
import { LoginCredentials } from "../interfaces/login-credentials";
import { RegisterCredentials } from "../interfaces/register-credentials";
import { ConfirmCredentials } from "../interfaces/confirm-credentials";
import { PasswordErrors } from "../interfaces/password-errors";

import { UserData } from "../interfaces/user-data";

const SIGNED_IN = "signedIn";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    public passwordRegex: RegExp = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}/;
    public minLength: number;

    private redirectTo: string | null = null;
    private signedIn: boolean = false;
    private user: any;
    private authState$: ReplaySubject<AuthState> = new ReplaySubject<AuthState>(null);
    private signoutCallbacks: Function[] = [];

    constructor(
        private config: ConfigService,
        private amplify: AmplifyService,
        private router: Router,
    ) {
        this.amplify.authStateChange$.subscribe((authState: AuthState) => {
            this.authState$.next(authState);
        });
        this.minLength = this.config.get("register.password.min_length");
    }

    public onSignout(fn: () => Promise<any>): void {
        if (!this.signoutCallbacks.includes(fn)) {
            this.signoutCallbacks.push(fn);
        }
    }

    // public watchAuthState(): Observable<AuthState> {
    //     return this.authState$.asObservable();
    // }

    public getAuthState(): Observable<AuthState> {
        return this.authState$.asObservable().pipe(take(1));
    }

    public getUser(): Promise<UserData> {
        return this.amplify.auth().currentAuthenticatedUser().then(user => ({
            username: user.username,
            email: user.attributes.email,
            sub: user.attributes.sub,
            role: user.attributes["custom:ROLE"] || "0",
        }));
    }

    public getSignedIn(): Observable<boolean> {
        return this.getAuthState().pipe(take(1), map(s => s.state === SIGNED_IN));
    }

    public getSessionIdJWT(): Observable<string> {
        return this.getAuthState().pipe(take(1), map(s => this.getJWT(s.user)));
    }

    public setRedirectTo(url: string): void {
        this.redirectTo = url;
    }

    public redirect(): void {
        if (this.redirectTo) {
            this.router.navigate([this.redirectTo]);
        } else {
            this.router.navigate(["/home"]);
        }
        this.redirectTo = null;
    }

    public async signout(): Promise<any> {
        await this.runSignoutCallbacks();
        return this.amplify.auth().signOut();
    }

    public async register(creds: RegisterCredentials): Promise<any> {
        console.log("Registering: ", creds);
        let custom_role = creds.educator ? "1" : "0";
        // return false;
        return this.amplify.auth().signUp({
            username: creds.username.trim(),
            password: creds.password.trim(),
            attributes: {
                email: creds.email.trim(),
                preferred_username: creds.username.trim(),
                "custom:ROLE": custom_role,
                // "custom:ROLE": "0",
                // "custom:ROLE": (!!creds.educator === true) ? "1" : "0"
            },
            validationData: [],
        });
    }

    public async confirm(creds: ConfirmCredentials): Promise<any> {
        return this.amplify.auth().confirmSignUp(creds.username.trim(), creds.confcode.trim());
    }

    public async signin(creds: LoginCredentials): Promise<any> {
        return this.amplify.auth().signIn(creds.username.trim(), creds.password.trim());
    }

    public async resendConfirm(username: string): Promise<any> {
        return this.amplify.auth().resendSignUp(username.trim());
    }

    public async changePassword(password: string, newPassword: string): Promise<any> {
        const user = await this.amplify.auth().currentAuthenticatedUser();
        return this.amplify.auth().changePassword(user, password.trim(), newPassword.trim());
    }

    public async forcePasswordUpdate(password: string): Promise<any> {
        const { user } = await this.getAuthState().toPromise();
        return this.amplify.auth().completeNewPassword(user, password.trim());
    }

    public async forgotPassword(username: string): Promise<any> {
        return this.amplify.auth().forgotPassword(username);
    }

    public async confirmPassword(username: string, code: string, password: string): Promise<any> {
        return this.amplify.auth().forgotPasswordSubmit(username, code, password);
    }

    public validatePassword(password: string = ""): PasswordErrors | null {
        if (this.passwordRegex.test(password)) {
            return null;
        }

        const errs: any = {};
        if (!/[A-Z]/.test(password)) {
            errs.needUppercase = true;
        }
        if (!/[0-9]/.test(password)) {
            errs.needNumber = true;
        }
        if (password.length < this.minLength) {
            errs.tooShort = true;
        }
        return errs;
    }

    private getJWT(user: any): string {
        return (user ? user.getSignInUserSession().idToken.jwtToken : "");
    }

    private runSignoutCallbacks(): Promise<any> {
        return Promise.all(this.signoutCallbacks.map(fn => fn()));
    }
}
